import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { SmartDropdownComponent } from '~common/smart-dropdown/smart-dropdown.component';
import { Account } from '~proto/account/account_pb';
import { Payload } from '~proto/payload/payload_pb';
import { IdName } from '~proto/types/types_pb';
import { idArrayToRecord } from '~utilities/idArrayToRecord';
import { ContractsService } from '../../services/contracts.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ct-order-details-edit-trucking-vendor-selector',
  styleUrls: ['./order-details-edit-trucking-vendor-selector.component.scss'],
  templateUrl: './order-details-edit-trucking-vendor-selector.component.html',
})
export class OrderDetailsEditTruckingVendorSelectorComponent implements OnInit {
  @Output() public selected = new EventEmitter<{ truckingVendor: IdName.AsObject }>();
  @ViewChild('smartDropdown', { static: false }) private smartDropdown: SmartDropdownComponent;
  @Input() public truckingVendor: Account.AsObject;
  @Input() public disabled = false;

  @Input()
  public set payloads(payloads: Payload.AsObject[]) {
    this._payloads$.next(payloads);
  }

  @Input()
  public set contractType(contractTypeId: number) {
    this.contractTypeId$.next(contractTypeId);
  }

  private topResult: IdName.AsObject;
  public textChanges$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public truckingVendors$: Observable<IdName.AsObject[]>;
  private _payloads$ = new BehaviorSubject<Payload.AsObject[]>([]);
  private contractTypeId$ = new BehaviorSubject<number>(-1);

  constructor(private contractService: ContractsService) {}

  public ngOnInit() {
    const uniqueVendorsByPayload$ = combineLatest([
      this.contractService.nonExpiredContracts$,
      this._payloads$,
      this.contractTypeId$,
    ]).pipe(
      map(([contracts, payloads, contractTypeId]) => {
        if (!payloads) {
          return [];
        }
        console.log('Payloads are', payloads);
        const unarchived = contracts.filter((contract) => !contract.archived);
        const uniqueVendors: Record<string, IdName.AsObject> = unarchived.reduce((acc, contract) => {
          const payloadIDsSupportedByContract = idArrayToRecord(contract.payloadtypesList);
          if (
            (contractTypeId <= 0 || (contract.contractType && contract.contractType.id === contractTypeId)) &&
            payloads.every((payload) => payloadIDsSupportedByContract[payload.type.id])
          ) {
            return { ...acc, [contract.vendor.id]: contract.vendor };
          }
          return acc;
        }, {});
        return Object.values(uniqueVendors).sort((a, b) =>
          a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()),
        );
      }),
    );
    this.truckingVendors$ = combineLatest([
      uniqueVendorsByPayload$,
      this.textChanges$$.pipe(
        debounceTime(100),
        startWith(''),
      ),
    ]).pipe(
      map(([contracts, text]) => {
        if (!contracts) {
          return [];
        }
        if (!text || text === '') {
          this.topResult = contracts.length ? contracts[0] : null;
          return contracts;
        }
        const asLowercase = text.toLocaleLowerCase();
        const filtered = contracts.filter((account) => account.name.toLocaleLowerCase().includes(asLowercase));
        this.topResult = filtered.length ? filtered[0] : null;
        return filtered;
      }),
    );
  }

  public inputTextChange(event: string) {
    this.textChanges$$.next(event.trim());
  }

  public selectVendor(truckingVendor: IdName.AsObject) {
    this.selected.emit({ truckingVendor });
    this.smartDropdown.close();
  }

  public enterKeyPressed() {
    if (this.topResult) {
      this.selectVendor(this.topResult);
    }
  }
}
